/* eslint-disable max-lines */
import { PrivateRoute } from "@src/components/routing";
import { loadLazily } from "@src/utils/loadLazily";
import { routes } from "@src/appV2/redesign/utils/routes";
import { Route } from "react-router-dom";

import { useWorkplaceMenuPriviligesProviderContext } from "./priviligesProviders/WorkplaceMenuPriviligesProvider";
import { HelpCenterPage } from "@src/appV2/HelpCenter/Page";

const FacilityOnboarding = loadLazily(
  async () =>
    await import("@src/containers/facilityOnboarding/FacilityOnboarding").then((module) => ({
      default: module.FacilityOnboarding,
    }))
);

const DailyView = loadLazily(
  async () =>
    await import("@src/appV2/redesign/DailyView/DailyView").then((module) => ({
      default: module.DailyView,
    }))
);

const AgentExperience = loadLazily(
  async () =>
    await import("@src/containers/app/agentExperience").then((module) => ({
      default: module.AgentExperience,
    }))
);

const AgentDashboard = loadLazily(
  async () =>
    await import("@src/containers/agentDashboard").then((module) => ({
      default: module.AgentDashboard,
    }))
);

const FacilityDashboard = loadLazily(
  async () =>
    await import("@src/containers/facilityDashboard").then((module) => ({
      default: module.FacilityDashboard,
    }))
);

const ChartingReportDateRangePicker = loadLazily(
  async () =>
    await import("@src/appV2/Charting/components/ChartingReportDateRangePicker").then((module) => ({
      default: module.ChartingReportDateRangePicker,
    }))
);

const MyProfessionalsPageWrapper = loadLazily(
  async () =>
    await import(
      "@src/containers/workers/Professionals/MyProfessionals/MyProfessionalsPageWrapper"
    ).then((module) => ({
      default: module.MyProfessionalsPageWrapper,
    }))
);

const WorkersDirectory = loadLazily(
  async () =>
    await import("@src/containers/workplace/WorkersDirectory/WorkersDirectory").then((module) => ({
      default: module.WorkersDirectoryPage,
    }))
);

const JobsLandingPage = loadLazily(
  async () =>
    await import("@src/appV2/JobsLandingPage/Page").then((module) => ({
      default: module.JobsLandingPage,
    }))
);

const ManageJobsTabs = loadLazily(
  async () =>
    await import("@src/appV2/ManageJobs/ManageJobsTabs").then((module) => ({
      default: module.ManageJobsTabs,
    }))
);

const PlacementCandidates = loadLazily(
  async () =>
    await import("@src/appV2/PlacementCandidates/Page").then((module) => ({
      default: module.PlacementCandidatesPage,
    }))
);

const InterviewSettings = loadLazily(
  async () =>
    await import("@src/appV2/InterviewSettings/Page").then((module) => ({
      default: module.InterviewSettingsPage,
    }))
);

const WorkplaceCharting = loadLazily(
  async () =>
    await import("@src/appV2/Charting/components/ChartingCompletionReport").then((module) => ({
      default: module.ChartingCompletionReport,
    }))
);

const AnalyticsDashboard = loadLazily(
  async () =>
    await import("@src/appV2/Analytics/components/AnalyticsDashboard").then((module) => ({
      default: module.AnalyticsDashboard,
    }))
);

const LiveMapView = loadLazily(
  async () =>
    await import("@src/containers/liveMapView").then((module) => ({
      default: module.LiveMapView,
    }))
);

const MyAccount = loadLazily(
  async () =>
    await import("@src/containers/myAccount").then((module) => ({
      default: module.MyAccount,
    }))
);

const TeamMembers = loadLazily(
  async () =>
    await import("@src/containers/teamMembers").then((module) => ({
      default: module.TeamMembers,
    }))
);

const FacilitySettings = loadLazily(
  async () =>
    await import("@src/containers/facilityDashboard/FacilitySettings/Page").then((module) => ({
      default: module.FacilitySettingsPage,
    }))
);

const FacilityTimeClockSettings = loadLazily(
  async () =>
    await import("@src/containers/workplaceSettings").then((module) => ({
      default: module.CustomSettings,
    }))
);

const FacilityUpcomingChargesV2 = loadLazily(
  async () =>
    await import("@src/appV2/FacilityUpcomingCharges").then((module) => ({
      default: module.FacilityUpcomingCharges,
    }))
);

const FacilityInvoiceHistory = loadLazily(
  async () =>
    await import("@src/appV2/FacilityInvoiceHistory").then((module) => ({
      default: module.FacilityInvoiceHistory,
    }))
);

const ContractTerms = loadLazily(
  async () =>
    await import("@src/containers/contractTerms").then((module) => ({
      default: module.ContractTerms,
    }))
);

const WorkplaceReferAndEarn = loadLazily(
  async () =>
    await import("@src/containers/workplaceReferAndEarn/WorkplaceReferAndEarn").then((module) => ({
      default: module.WorkplaceReferAndEarn,
    }))
);

export function WorkplaceRoutes(props: { isAgent: boolean }): JSX.Element {
  const { isAgent } = props;

  const workplaceMenuPriviliges = useWorkplaceMenuPriviligesProviderContext();

  return (
    <>
      <PrivateRoute
        exact
        path={routes.facilityOnboarding}
        component={FacilityOnboarding}
        isAuthorized={workplaceMenuPriviliges.workplaceOnboarding}
      />
      <PrivateRoute
        path={routes.dailyView}
        component={DailyView}
        isAuthorized={workplaceMenuPriviliges.dailyView}
      />
      <Route path={routes.agentDashboard} component={isAgent ? AgentExperience : AgentDashboard} />
      <PrivateRoute
        path={routes.facilityDashboard}
        component={FacilityDashboard}
        isAuthorized={workplaceMenuPriviliges.calendar}
      />
      <PrivateRoute
        path={routes.facilityChatWithAgent}
        component={ChartingReportDateRangePicker}
        isAuthorized={workplaceMenuPriviliges.chat}
      />
      <PrivateRoute
        exact
        path={routes.workplaceWorkers}
        component={MyProfessionalsPageWrapper}
        isAuthorized={workplaceMenuPriviliges.professionalsSection?.myProfessionals}
      />
      <PrivateRoute
        exact
        path={routes.workersDirectory}
        component={WorkersDirectory}
        isAuthorized={workplaceMenuPriviliges.professionalsSection?.browseProfessionals}
      />
      <PrivateRoute
        exact
        path={routes.jobsLandingPage}
        component={JobsLandingPage}
        isAuthorized={workplaceMenuPriviliges.permanentHiringSection?.placementsLandingPage}
      />
      <PrivateRoute
        exact
        path={routes.manageJobs}
        component={ManageJobsTabs}
        isAuthorized={workplaceMenuPriviliges.permanentHiringSection?.applicationAndJobs}
      />
      <PrivateRoute
        exact
        path={routes.manageJobs}
        component={ManageJobsTabs}
        isAuthorized={workplaceMenuPriviliges.permanentHiringSection?.applicationAndJobs}
      />
      <PrivateRoute
        exact
        path={routes.workplaceHireProfessionals}
        component={PlacementCandidates}
        isAuthorized={workplaceMenuPriviliges.permanentHiringSection?.browseTalentPool}
      />
      <PrivateRoute
        exact
        path={routes.interviewSettings}
        component={InterviewSettings}
        isAuthorized={workplaceMenuPriviliges.permanentHiringSection?.interviewAvailability}
      />
      <PrivateRoute
        exact
        path={routes.workplaceCharting}
        component={WorkplaceCharting}
        isAuthorized={workplaceMenuPriviliges.charting}
      />
      <PrivateRoute
        exact
        path={routes.workplaceAnalytics}
        component={AnalyticsDashboard}
        isAuthorized={workplaceMenuPriviliges.analytics}
      />
      <PrivateRoute
        exact
        path={routes.facilityLiveWorkerETA}
        component={LiveMapView}
        isAuthorized={workplaceMenuPriviliges.liveWorkerETA}
      />
      <PrivateRoute
        exact
        path={routes.myAccount}
        component={MyAccount}
        isAuthorized={workplaceMenuPriviliges.managementSection?.myAccount}
      />
      <PrivateRoute
        exact
        path={routes.workplaceUsers}
        component={TeamMembers}
        isAuthorized={workplaceMenuPriviliges.managementSection?.teamMembers}
      />
      <PrivateRoute
        exact
        path={routes.facilitySettings}
        component={FacilitySettings}
        isAuthorized={workplaceMenuPriviliges.managementSection?.settings}
      />
      <PrivateRoute
        exact
        path={routes.facilityTimeclockSettings}
        component={FacilityTimeClockSettings}
        isAuthorized={workplaceMenuPriviliges.managementSection?.timeclockingSettings}
      />
      <PrivateRoute
        exact
        path={routes.facilityInvoiceUpcomingCharges}
        component={FacilityUpcomingChargesV2}
        isAuthorized={workplaceMenuPriviliges.billingSection?.upcomingCharges}
      />
      <PrivateRoute
        exact
        path={routes.facilityInvoiceHistory}
        component={FacilityInvoiceHistory}
        isAuthorized={workplaceMenuPriviliges.billingSection?.invoiceHistory}
      />
      <PrivateRoute
        exact
        path={routes.facilityInvoiceContractTerms}
        component={ContractTerms}
        isAuthorized={workplaceMenuPriviliges.billingSection?.contractTerms}
      />
      <PrivateRoute
        exact
        path={routes.helpCenter}
        component={HelpCenterPage}
        isAuthorized={workplaceMenuPriviliges.helpCenter}
      />
      <PrivateRoute
        exact
        path={routes.workplaceReferAndEarn}
        component={WorkplaceReferAndEarn}
        isAuthorized={workplaceMenuPriviliges.referAndEarn}
      />
    </>
  );
}
/* eslint-enable max-lines */
